/**
 * Shared API Client Configuration
 * This module exports the axios client configured with the backend URL
 */
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({ baseURL: API });

// Add auth token interceptor
apiClient.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response error handling
apiClient.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      // Token expired or invalid - clear and redirect
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    // Normalize FastAPI/Pydantic validation errors.
    //
    // FastAPI returns 422 errors as:
    //   { detail: [{ type, loc, msg, input, url }, ...] }
    //
    // Hundreds of call-sites do `err.response?.data?.detail` and feed it
    // straight into a toast or JSX. When `detail` is an array of objects
    // (instead of a string) React renders it and crashes with error #31
    // ("Objects are not valid as a React child"). We collapse the array
    // into a human-readable string IN PLACE here, so every existing call
    // site keeps working without per-page edits.
    const data = error.response?.data;
    if (data && Array.isArray(data.detail)) {
      const parts = data.detail
        .map((d) => {
          if (!d || typeof d !== 'object') return String(d ?? '');
          const field = Array.isArray(d.loc)
            // Drop the leading 'body'/'query'/'path' scope marker — the
            // user only cares about the field name.
            ? d.loc.filter((s) => s !== 'body' && s !== 'query' && s !== 'path').join('.')
            : '';
          const msg = d.msg || d.message || 'Invalid value';
          return field ? `${field}: ${msg}` : msg;
        })
        .filter(Boolean);
      data.detail = parts.length ? parts.join('; ') : 'Validation error';
    }
    return Promise.reject(error);
  }
);

export { apiClient, API, BACKEND_URL };
export default apiClient;
